import type { FC } from 'react';
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';

import { ArchivedPageNoteInput } from '../ArchivedPageNoteInput';

import {
	NoteActionsWrapper,
	ArchivePageActionLink,
	HorizontalSeparator,
} from './archivePageBannerNoteSharedStyles';

const wrapperStyles = xcss({
	marginTop: 'space.100',
});

export const i18n = defineMessages({
	updateNote: {
		id: 'archive-pages.page-banner.note.actions.update-note',
		defaultMessage: 'Update',
		description:
			'button text that allows the user to save the updated descriptive note to the archived page',
	},
	saveNote: {
		id: 'archive-pages.page-banner.note.actions.save-note',
		defaultMessage: 'Add',
		description:
			'button text that allows the user to save the new descriptive note to the archived page that didnt have a note before.',
	},
	cancelSaveNote: {
		id: 'archive-pages.page-banner.note.actions.cancel-note',
		defaultMessage: 'Cancel',
		description:
			'button text that allows the user to cancel their changes to the descriptive note on the archived page',
	},
});
type ArchivedPageBannerEditNoteProps = {
	defaultValue: string;
	onSave: (note: string) => void;
	onCancel: () => void;
};

const ArchivedPageBannerEditNoteComponent: FC<ArchivedPageBannerEditNoteProps> = ({
	defaultValue,
	onSave,
	onCancel,
}) => {
	const createNoteRef = useRef<HTMLTextAreaElement>(null);
	const [isSaveDisabled, setIsSaveDisabled] = useState(false);

	useEffect(() => {
		createNoteRef.current?.focus();

		// move cursor to the end of the textarea. its at the start by default.
		const noteLength = createNoteRef.current?.value?.length || 0;
		createNoteRef.current?.setSelectionRange(noteLength, noteLength);
	}, [createNoteRef]);

	const handleSave = useCallback(() => {
		onSave(createNoteRef.current?.value || '');
	}, [onSave, createNoteRef]);

	return (
		<Box xcss={wrapperStyles}>
			<ArchivedPageNoteInput
				noteRef={createNoteRef}
				defaultValue={defaultValue}
				analyticsSource="archiveList"
				onValidate={(isValid) => {
					setIsSaveDisabled(!isValid);
				}}
				isColorInverted
			/>
			<NoteActionsWrapper>
				<ArchivePageActionLink isDisabled={isSaveDisabled} onClick={handleSave}>
					<FormattedMessage {...(defaultValue ? i18n.updateNote : i18n.saveNote)} />
				</ArchivePageActionLink>
				<HorizontalSeparator />
				<ArchivePageActionLink onClick={onCancel}>
					<FormattedMessage {...i18n.cancelSaveNote} />
				</ArchivePageActionLink>
			</NoteActionsWrapper>
		</Box>
	);
};

export const ArchivedPageBannerEditNote = ArchivedPageBannerEditNoteComponent;
